import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, Info, ChevronDown, ChevronUp, Lock } from "lucide-react";

/**
 * Standard Legal Clauses Review.
 * Lists the clauses that will appear in the generated will. Mandatory clauses
 * are locked. Optional clauses can be toggled off to omit them from the final
 * PDF / DOCX. Each clause is expandable to read the exact wording.
 */
export default function ClausesStep({ clauseToggles, setClauseToggles }) {
  const mandatory = [
    {
      id: "revocation",
      title: "Revocation of Prior Wills",
      preview:
        "Revokes every will, codicil and testamentary disposition you have made earlier.",
      body:
        "I hereby revoke all former Wills, Codicils and testamentary dispositions made by me at any time heretofore and declare this to be my Last Will and Testament.",
    },
    {
      id: "sound_mind",
      title: "Sound Mind Declaration",
      preview:
        "Confirms you are of sound mind and acting without coercion or undue influence.",
      body:
        "I declare that I am of sound mind, memory and understanding and not acting under any duress, coercion, undue influence or fraud. I make this Will voluntarily and of my own free will.",
    },
    {
      id: "attestation",
      title: "Attestation by Two Witnesses",
      preview:
        "Standard signing block where you and two adult witnesses sign the will in each other's presence.",
      body:
        "IN WITNESS WHEREOF, I have hereunto set my hand on this day, at the place mentioned above, in the presence of the two attesting witnesses below, both of whom have signed in my presence and in the presence of each other.",
    },
  ];

  const optional = [
    {
      id: "debts",
      title: "Payment of Debts & Funeral Expenses",
      preview:
        "Directs the Executor to first pay your debts, taxes, medical bills and funeral expenses from the estate.",
      body:
        "I direct that all my lawful debts, liabilities, taxes, medical expenses, funeral expenses and testamentary expenses shall first be paid out of my estate by my Executor before distribution to beneficiaries.",
    },
    {
      id: "residuary",
      title: "Residuary Estate",
      preview:
        "Covers any asset not specifically listed — applies the same proportions to anything you acquire later.",
      body:
        "Any asset, property, investment, entitlement or interest not specifically mentioned in this Will, whether presently owned or acquired in the future, shall form part of my residuary estate and shall be distributed in the same proportions set out in this Will.",
    },
    {
      id: "executor_powers",
      title: "Executor Powers Paragraph",
      preview:
        "Grants your Executor full authority to manage, sell, transfer, encash, invest and distribute the estate.",
      body:
        "My Executor shall have full authority to manage, sell, transfer, encash, invest, settle, distribute or otherwise deal with my estate as may be reasonably necessary for administering this Will and carrying out my wishes.",
    },
    {
      id: "digital_assets",
      title: "Digital Assets Clause",
      preview:
        "Authorises the Executor to access and manage your email, social media, crypto wallets and other online accounts.",
      body:
        "I authorize my Executor to access, manage, transfer, archive or delete my digital assets and online accounts in accordance with applicable law and platform policies.",
    },
    {
      id: "funeral_wishes",
      title: "Funeral & Last Rites Wishes",
      preview:
        "Records your preferences for funeral ceremonies and last rites (only if you provided any).",
      body:
        "It is my wish that my funeral ceremonies and last rites be conducted in accordance with my personal beliefs and family customs.",
    },
  ];

  const toggle = (id) =>
    setClauseToggles({ ...clauseToggles, [id]: !clauseToggles[id] });

  return (
    <div data-testid="step-clauses">
      <h2 className="font-serif text-2xl text-navy">Standard Legal Clauses</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        Here are the clauses that will appear in your will. The first three are mandatory under
        Indian succession law. The rest are recommended — switch any off if you do not want it
        included in your final document.
      </p>

      <div
        className="mt-6 flex items-start gap-3 rounded-md border border-gold/40 bg-[#FBF7EE] p-4 text-sm text-navy"
        data-testid="clauses-witness-callout"
      >
        <Info size={18} className="mt-0.5 shrink-0 text-gold" />
        <div>
          <div className="font-medium">Witness rules — please remember</div>
          <p className="mt-1 text-[#4A5568]">
            Your will must be signed in the presence of <span className="font-medium text-navy">two adult witnesses</span> (18+).
            Witnesses must be of sound mind and{" "}
            <span className="font-medium text-navy">not beneficiaries</span> under this will,
            nor the spouse of any beneficiary. Both witnesses must sign in your presence and
            in each other's presence.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <div className="mb-3 flex items-center gap-2">
          <Lock size={14} className="text-navy" />
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy">
            Mandatory clauses (always included)
          </div>
        </div>
        <div className="space-y-3" data-testid="mandatory-clauses-list">
          {mandatory.map((c) => (
            <ClauseRow key={c.id} clause={c} locked />
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck size={14} className="text-gold" />
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy">
            Recommended clauses (toggle to include / exclude)
          </div>
        </div>
        <div className="space-y-3" data-testid="optional-clauses-list">
          {optional.map((c) => (
            <ClauseRow
              key={c.id}
              clause={c}
              checked={!!clauseToggles[c.id]}
              onToggle={() => toggle(c.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ClauseRow({ clause, locked = false, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <Card
      className={`border-[#0A1128]/10 ${
        !locked && !checked ? "bg-[#FAFAFA]" : "bg-white"
      }`}
      data-testid={`clause-${clause.id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-serif text-base text-navy">{clause.title}</div>
              {locked && (
                <span className="inline-flex items-center gap-1 rounded-full border border-navy/20 bg-navy/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy">
                  <Lock size={10} />
                  Required
                </span>
              )}
              {!locked && checked === false && (
                <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700">
                  Excluded
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-[#4A5568]">{clause.preview}</p>
          </div>
          <div className="shrink-0">
            {locked ? (
              <span className="inline-block h-6 w-11 rounded-full bg-navy/80 px-1">
                <span className="block h-4 w-4 translate-x-5 translate-y-1 rounded-full bg-gold" />
              </span>
            ) : (
              <Switch
                checked={!!checked}
                onCheckedChange={onToggle}
                data-testid={`clause-toggle-${clause.id}`}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-navy/80 hover:text-navy"
          data-testid={`clause-expand-${clause.id}`}
        >
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          {open ? "Hide clause text" : "Read clause text"}
        </button>
        {open && (
          <div
            className="mt-3 rounded-md border border-[#0A1128]/10 bg-[#FBFAF6] p-4 font-serif text-sm leading-relaxed text-[#0A1128]/90"
            data-testid={`clause-body-${clause.id}`}
          >
            {clause.body}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
